import React from 'react';
import { Box, Text, Flex, VStack } from '@chakra-ui/react';
import { surveyTheme as theme } from './surveyTheme';
import { t } from './translations';

/**
 * MultiSelectQuestion - Choose up to N options
 *
 * Props:
 *   question: { choices: [{id, text}], max_selections: number }
 *   selectedIds: string[] - currently selected choice IDs
 *   onToggle: (choiceId) => void
 *   maxSelections: number | null
 */
const MultiSelectQuestion = ({ question, selectedIds = [], onToggle, maxSelections, lang = 'en' }) => {
  const atLimit = maxSelections && selectedIds.length >= maxSelections;

  return (
    <VStack spacing={theme.spacing.choiceGap} align="stretch">
      {/* Selection counter */}
      {maxSelections && (
        <Text
          fontSize="14px"
          fontWeight={500}
          color={atLimit ? theme.colors.teal : theme.colors.textMuted}
          fontFamily={theme.typography.fontFamily}
          textAlign="center"
        >
          {t(lang, 'selectedOf', { count: selectedIds.length, max: maxSelections })}
        </Text>
      )}

      {question.choices.map((choice) => {
        const isSelected = selectedIds.includes(String(choice.id));
        const isDisabled = !isSelected && atLimit;

        return (
          <Box
            key={choice.id}
            as="button"
            type="button"
            onClick={() => !isDisabled && onToggle(choice.id)}
            width="100%"
            textAlign="left"
            bg={isSelected ? theme.colors.teal : theme.colors.surface}
            color={isSelected ? 'white' : isDisabled ? theme.colors.textMuted : theme.colors.textSecondary}
            border="1.5px solid"
            borderColor={isSelected ? theme.colors.teal : theme.colors.borderLight}
            borderRadius={theme.radii.button}
            padding={theme.spacing.buttonPadding}
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            opacity={isDisabled ? 0.5 : 1}
            transition={`background-color ${theme.transitions.normal}, border-color ${theme.transitions.normal}, color ${theme.transitions.normal}, opacity ${theme.transitions.normal}`}
            boxShadow={isSelected ? theme.shadows.selected : 'none'}
            _hover={!isDisabled ? {
              borderColor: isSelected ? theme.colors.teal : theme.colors.teal,
              boxShadow: isSelected ? theme.shadows.selected : theme.shadows.subtle,
            } : {}}
            _focus={{
              outline: 'none',
              boxShadow: `0 0 0 3px rgba(59, 107, 107, 0.3)`,
            }}
            _active={!isDisabled ? { transform: 'scale(0.99)' } : {}}
            role="checkbox"
            aria-checked={isSelected}
            aria-disabled={isDisabled}
          >
            <Flex align="center" gap={3}>
              {/* Checkbox indicator */}
              <Box
                w="20px"
                h="20px"
                flexShrink={0}
                borderRadius="4px"
                border="2px solid"
                borderColor={isSelected ? 'white' : isDisabled ? theme.colors.borderLight : theme.colors.borderMedium}
                bg={isSelected ? 'rgba(255,255,255,0.2)' : 'transparent'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                transition={`all ${theme.transitions.fast}`}
              >
                {isSelected && (
                  <Box as="svg" w="12px" h="12px" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Box>
                )}
              </Box>

              <Text
                fontSize={theme.typography.choice.fontSize}
                fontWeight={isSelected ? 500 : theme.typography.choice.fontWeight}
                lineHeight={theme.typography.choice.lineHeight}
                fontFamily={theme.typography.fontFamily}
              >
                {(lang === 'es' && choice.text_es) ? choice.text_es : choice.text}
              </Text>
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );
};

export default MultiSelectQuestion;
